import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  FaShoppingCart, FaChartLine, FaStar, FaHeart, FaRegHeart,
  FaChevronLeft, FaChevronRight, FaTimes, FaFlask, FaFire,
  FaPlus, FaCheck
} from "react-icons/fa";
import { useLocationContext } from "../../../context/LocationContext";
import { useCartActions } from "../../../hooks/useCartActions";
import TestDetails from "../../LabTest/TestDetails";
import Coupon from "../../Coupon/Coupon";
import {
  fetchWishlist,
  addToWishlist,
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../../Redux/wishlistSlice";
import { fetchAllRatings } from "../../../Redux/ratingSlice";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const BASE_IMG = API_BASE_URL?.replace('/api/', '').replace(/\/$/, '');

const getImageUrl = (img) => {
  if (!img || img === "" || img === "null" || img === "undefined") return null;
  let cleanImg = img.toString().trim();
  if (cleanImg.startsWith("http://") || cleanImg.startsWith("https://")) return cleanImg;
  cleanImg = cleanImg.replace(/^\/+/, '');
  if (cleanImg.includes("TestMasterImage")) return `${BASE_IMG}/${cleanImg}`;
  return `${BASE_IMG}/TestMasterImage/${encodeURIComponent(cleanImg)}`;
};

export default function AllTests() {
  const dispatch = useDispatch();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [showOnlyPopular, setShowOnlyPopular] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  const [showCompare, setShowCompare] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const { coords, selectedCity } = useLocationContext();
  const cart = useSelector((state) => state.cart.items || []);
  const { addWithReplaceCheck, removeItem } = useCartActions();
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);

  // ========== Helpers ==========
  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
    return user?.userId || null;
  };

  const isInWishlist = (productId, categoryId = 2) =>
    wishlistItems.some(
      item => Number(item.productId) === Number(productId) &&
              Number(item.categoryId) === Number(categoryId)
    );

  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (e) { console.error(e); }
  };

  const toggleWishlist = async (test, e) => {
    e.stopPropagation();
    const userId = getUserId();
    if (!userId) { alert('Please login to add to wishlist'); return; }
    const productId = test.TestID;
    const categoryId = 2;
    const isWished = isInWishlist(productId, categoryId);
    if (isWished) dispatch(removeWishlistOptimistic({ productId, categoryId }));
    else dispatch(addWishlistOptimistic({ productId, categoryId }));
    try {
      if (isWished) await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      else await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
    } catch (e) {
      console.error(e);
      if (isWished) dispatch(addWishlistOptimistic({ productId, categoryId }));
      else dispatch(removeWishlistOptimistic({ productId, categoryId }));
      alert('Failed to update wishlist. Please try again.');
    }
  };

  const getTestRating = (testId) => {
    const key = `${testId}_2`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  const renderStars = (rating = 0) => {
    const full = Math.floor(rating);
    return [...Array(5)].map((_, i) =>
      i < full ? <FaStar key={i} size={12} color="#ffc107" /> : <FaStar key={i} size={12} color="#ddd" />
    );
  };

  // ========== Fetch Tests ==========
  useEffect(() => {
    const fetchTests = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}Lab/GetTestAll?pageNumber=1&pageSize=1000`);
        const data = res?.data || [];
        const list = Array.isArray(data) ? data : data?.data || [];
        setTests(list);
        setCurrentPage(1);
        if (list.length) {
          const ratingProducts = list.map(t => ({ productId: t.TestID, categoryId: 2 }));
          await dispatch(fetchAllRatings(ratingProducts));
        }
        await loadWishlist();
      } catch (err) { console.error(err); setTests([]); } finally { setLoading(false); }
    };
    fetchTests();
  }, [dispatch]);

  // ========== Distance & Filtering ==========
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return 999;
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
    return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * R;
  };

  const filteredTests = tests.filter(t => {
    const testLat = Number(t.Latitude);
    const testLng = Number(t.Longnitude);
    if (isNaN(testLat) || isNaN(testLng)) return false;
    const sourceLat = selectedCity?.latitude ?? selectedCity?.lat ?? coords?.lat;
    const sourceLng = selectedCity?.longitude ?? selectedCity?.lng ?? coords?.lng;
    if (!sourceLat || !sourceLng) return true;
    const distance = getDistanceKm(Number(sourceLat), Number(sourceLng), testLat, testLng);
    if (distance > 20) return false;
    if (showOnlyPopular) return t.IsPopular === true;
    return true;
  });

  const totalPages = Math.ceil(filteredTests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTests = filteredTests.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) { setCurrentPage(cp => cp + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };
  const goToPreviousPage = () => {
    if (currentPage > 1) { setCurrentPage(cp => cp - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };
  const goToPage = page => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const getPageNumbers = () => {
    const pages = [];
    const max = 5;
    if (totalPages <= max) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // ========== Compare ==========
  const toggleCompare = (test) => {
    if (selectedForCompare.find(t => t.TestID === test.TestID)) {
      setSelectedForCompare(selectedForCompare.filter(t => t.TestID !== test.TestID));
    } else {
      if (selectedForCompare.length < 4) {
        setSelectedForCompare([...selectedForCompare, test]);
      } else {
        alert("You can compare maximum 4 tests at a time");
      }
    }
  };
  const clearCompare = () => { setSelectedForCompare([]); setShowCompare(false); };
  const removeFromCompare = (testId) => {
    setSelectedForCompare(selectedForCompare.filter(t => t.TestID !== testId));
  };
  const getAllParameters = () => {
    const set = new Set();
    selectedForCompare.forEach(test => {
      if (test.Parameters && Array.isArray(test.Parameters)) {
        test.Parameters.forEach(p => { if (p.ParameterName) set.add(p.ParameterName); });
      }
    });
    return Array.from(set);
  };
  const getParameterValue = (test, paramName) => {
    if (test.Parameters && Array.isArray(test.Parameters)) {
      const param = test.Parameters.find(p => p.ParameterName === paramName);
      return param ? param.Value || param.Range || "Present" : "—";
    }
    return "—";
  };

  // ========== Cart ==========
  const isInCart = (testID) => cart.some(i => Number(i.productId) === Number(testID) && Number(i.categoryId) === 2);
  const buildCartItem = (test) => ({
    productId: test.TestID,
    categoryId: 2,
    name: test.TestName,
    price: test.SellingPrice,
    quantity: 1,
    storeId: test.LabId || 0,
    originalPrice: test.TestPrice || 0,
    discount: (test.TestPrice || 0) - (test.SellingPrice || 0),
  });
  const handleAdd = (test) => addWithReplaceCheck(buildCartItem(test));
  const handleRemove = (test) => removeItem(buildCartItem(test));
  const handleImageError = (testId) => {
    if (!imageErrors[testId]) setImageErrors(prev => ({ ...prev, [testId]: true }));
  };

  // ========== Location loading ==========
  if (!coords?.lat || !coords?.lng) {
    return <div className="text-center py-10"><p>📍 Detecting your location...</p></div>;
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
          <FaFlask size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">ALL TESTS</span>
          <FaStar size={12} className="text-yellow-400" />
        </div>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">All</span>{" "}
          <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">Health Tests</span>
        </h2>
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">Complete range of diagnostic tests at best prices</p>
        <div className="flex justify-center gap-2 mt-4">
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-6 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-600"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-3">
        <h3 className="text-lg font-semibold text-gray-700">
          Showing {filteredTests.length} Tests
          {showOnlyPopular && <span className="ml-2 text-sm text-red-500 font-medium">(Popular Only)</span>}
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => { setShowOnlyPopular(!showOnlyPopular); setCurrentPage(1); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 text-sm font-medium ${
              showOnlyPopular ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md" : "bg-gray-200 text-gray-600 hover:bg-gray-300"
            }`}
          >
            <FaFire size={14} />
            {showOnlyPopular ? "Showing Popular" : "Show Popular"}
          </button>
          <button
            onClick={() => setShowCompare(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors duration-200 text-sm font-medium ${
              selectedForCompare.length === 0 ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-green-600 text-white hover:bg-green-700"
            }`}
            disabled={selectedForCompare.length === 0}
          >
            <FaChartLine size={16} />
            Compare ({selectedForCompare.length})
          </button>
        </div>
      </div>

      {/* Compare Bar */}
      {selectedForCompare.length > 0 && !showCompare && (
        <div className="mb-4 p-3 bg-red-50 rounded-md border border-red-200 shadow-sm">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-red-700 flex items-center gap-2">
              <FaChartLine className="text-red-500" />
              Selected for comparison ({selectedForCompare.length}):
            </span>
            <button onClick={clearCompare} className="text-xs text-red-600 hover:text-red-800 font-medium">Clear all</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedForCompare.map(test => (
              <div key={test.TestID} className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-red-200 shadow-sm">
                <span className="text-sm font-medium text-gray-700">{test.TestName}</span>
                <button onClick={() => removeFromCompare(test.TestID)} className="text-red-500 hover:text-red-700">
                  <FaTimes size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-red-500 border-t-transparent"></div>
          <p className="mt-3 text-gray-500 text-sm">Loading tests...</p>
        </div>
      )}

      {/* Grid */}
      <div className="w-full px-2">
        {currentTests.length === 0 && !loading ? (
          <div className="text-center py-10">
            <p className="text-gray-500">
              {showOnlyPopular ? "No popular tests found within 20km radius" : "No tests found within 20km radius"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {currentTests.map((test) => {
              const inCart = isInCart(test.TestID);
              const inWishlist = isInWishlist(test.TestID, 2);
              const originalPrice = Number(test.TestPrice) || 0;
              const sellingPrice = Number(test.SellingPrice) || originalPrice;
              const discountAmount = originalPrice - sellingPrice;
              const discountPercent = originalPrice > sellingPrice ? Math.round((discountAmount / originalPrice) * 100) : 0;
              const imageUrl = getImageUrl(test.TestImage);
              const hasImageError = imageErrors[test.TestID];
              const isSelected = selectedForCompare.find(t => t.TestID === test.TestID);
              const { average: avgRating, total: totalReviews } = getTestRating(test.TestID);
              const isPopular = test.IsPopular === true;
              const isPriceAvailable = sellingPrice > 0 || originalPrice > 0;

              const reportTime = test.ReportTime;
              const reportTimeType = test.ReportTimeType;
              let reportText = "";
              if (reportTime) {
                const unit = reportTimeType === 2 ? "days" : "hours";
                reportText = `⏱️ Reports in ${reportTime} ${unit}`;
              }

              let imageSrc = null;
              let showFallbackIcon = false;
              if (!hasImageError && imageUrl) {
                imageSrc = imageUrl;
              } else {
                showFallbackIcon = true;
              }

              const labId = test.LabId;

              return (
                <div
                  key={test.TestID}
                  className={`group/card relative bg-white border rounded-xl p-3 h-[380px] flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${
                    isSelected ? 'ring-2 ring-green-500' : ''
                  }`}
                  onClick={() => setSelectedTestId(test.TestID)}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute top-2 left-2 z-10">
                      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                        <FaStar size={9} className="fill-white" /> POPULAR
                      </div>
                    </div>
                  )}

                  {/* Discount Badge */}
                  {discountPercent > 0 && isPriceAvailable && (
                    <div className="absolute top-8 left-2 z-10">
                      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                        {discountPercent}% OFF
                      </div>
                    </div>
                  )}

                  {/* Wishlist Button */}
                  <button
                    onClick={(e) => toggleWishlist(test, e)}
                    className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                    disabled={wishlistLoading}
                  >
                    {wishlistLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      inWishlist ? <FaHeart size={14} className="text-red-500 fill-red-500" /> : <FaRegHeart size={14} className="text-gray-400 hover:text-red-500" />
                    )}
                  </button>

                  {/* Image - Zomato Style */}
                  <div
                    className="relative w-full h-[140px] cursor-pointer rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100"
                    onClick={() => setSelectedTestId(test.TestID)}
                  >
                    {!showFallbackIcon && imageSrc ? (
                      <img
                        src={imageSrc}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        alt={test.TestName}
                        onError={() => handleImageError(test.TestID)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaFlask size={48} className="text-gray-300" />
                      </div>
                    )}
                    {/* Image Gradient Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* Name */}
                  <div className="text-[14px] font-bold text-center line-clamp-2 mt-2.5 px-1 min-h-[40px] text-gray-800">
                    {test.TestName}
                  </div>

                  {/* Ratings */}
                  <div className="flex justify-center items-center gap-1 mt-1">
                    <div className="flex gap-0.5">{renderStars(avgRating || 0)}</div>
                    {!ratingLoading && totalReviews > 0 && (
                      <span className="text-[10px] text-gray-400 font-medium">({totalReviews} reviews)</span>
                    )}
                  </div>

                  {/* Lab Name */}
                  {test.LabName && (
                    <div className="text-[10px] text-gray-400 text-center truncate px-1 mt-1">
                      {test.LabName}
                    </div>
                  )}

                  {/* Report Time Badge */}
                  {reportText && (
                    <div className="flex justify-center items-center mt-1.5">
                      <span className="text-[10px] font-medium text-red-600 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                        {reportText}
                      </span>
                    </div>
                  )}

                  {/* Price & Buttons OR Coupon */}
                  <div className="mt-auto pt-2.5 border-t border-gray-100">
                    {discountPercent > 0 && isPriceAvailable ? (
                      // ---------- DISCOUNT SECTION ----------
                      <div className="flex justify-between items-center">
                        <div className="flex flex-col">
                          <div className="text-lg font-extrabold text-green-600">
                            ₹{sellingPrice}
                          </div>
                          {originalPrice > 0 && (
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs line-through text-gray-400">
                                ₹{originalPrice}
                              </span>
                              <span className="text-[11px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-md">
                                Save ₹{discountAmount}
                              </span>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            inCart ? handleRemove(test) : handleAdd(test);
                          }}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                            ${inCart ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100" : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"}`}
                        >
                          <FaShoppingCart size={10} />
                          {inCart ? "Remove" : "Add"}
                        </button>
                      </div>
                    ) : (
                      // ---------- COUPON SECTION ----------
                      <div className="flex flex-col gap-1">
                        {/* Price and Add to Cart in same row */}
                        <div className="flex justify-between items-center">
                          {/* Price */}
                          {isPriceAvailable && (
                            <div className="flex flex-col">
                              <div className="text-sm font-bold text-green-600">
                                ₹{sellingPrice || originalPrice}
                              </div>
                              {originalPrice > 0 && sellingPrice > 0 && originalPrice !== sellingPrice && (
                                <span className="text-xs line-through text-gray-400">
                                  ₹{originalPrice}
                                </span>
                              )}
                              {discountPercent === 0 && originalPrice > 0 && sellingPrice > 0 && originalPrice === sellingPrice && (
                                <span className="text-[9px] text-gray-400">No Discount</span>
                              )}
                            </div>
                          )}

                          {/* Add to Cart Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              inCart ? handleRemove(test) : handleAdd(test);
                            }}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                              ${inCart ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100" : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"}`}
                          >
                            <FaShoppingCart size={10} />
                            {inCart ? "Remove" : "Add"}
                          </button>
                        </div>

                        {/* Coupon Component - Below Price & Add to Cart */}
                        <Coupon
                          labId={labId}
                          userId={getUserId()}
                          testPrice={originalPrice}
                          sellingPrice={sellingPrice}
                          discount={discountAmount}
                          showCopyButton={false}
                          showAddToCart={false}
                          isInCart={inCart}
                          onAddToCart={() => handleAdd(test)}
                          onRemoveFromCart={() => handleRemove(test)}
                          className="w-full"
                          loadingText="Loading coupon..."
                          noCouponText="No coupon available"
                          labelText="🎫 No Discount - Coupon"
                        />
                      </div>
                    )}

                    {/* Compare Section */}
                    <div className="flex justify-end pt-0.5 mt-1 border-t border-gray-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCompare(test);
                        }}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                          ${isSelected ? "bg-green-50 text-green-600 border-green-500 hover:bg-green-100" : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"}`}
                      >
                        {isSelected ? <FaCheck size={10} /> : <FaPlus size={10} />}
                        {isSelected ? "Comparing" : "Compare"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8 mb-4 flex-wrap">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 ${
              currentPage === 1 ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            <FaChevronLeft size={12} /> Previous
          </button>
          <div className="flex gap-1 flex-wrap">
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === 'number' && goToPage(page)}
                className={`px-3 py-2 rounded-md transition-colors duration-200 min-w-[40px] ${
                  currentPage === page ? "bg-red-600 text-white" : page === '...' ? "bg-gray-100 text-gray-600 cursor-default" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                disabled={page === '...'}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 ${
              currentPage === totalPages ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Next <FaChevronRight size={12} />
          </button>
        </div>
      )}
      {!loading && filteredTests.length > 0 && (
        <div className="text-center text-sm text-gray-500 mt-2">
          Showing {startIndex + 1} - {Math.min(endIndex, filteredTests.length)} of {filteredTests.length} tests
          {showOnlyPopular && <span className="ml-2 text-red-500 font-medium">(Popular only)</span>}
        </div>
      )}

      {/* Compare Modal */}
      {showCompare && selectedForCompare.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Compare Tests</h3>
              <button onClick={() => setShowCompare(false)} className="text-gray-500 hover:text-gray-700">
                <FaTimes size={20} />
              </button>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-[60px] bg-gray-50">
                  <tr>
                    <th className="border p-3 text-left bg-gray-100 w-48">Parameters</th>
                    {selectedForCompare.map(test => (
                      <th key={test.TestID} className="border p-3 text-left min-w-[200px]">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-red-600">{test.TestName}</h4>
                            <p className="text-xs text-gray-500">{test.LabName}</p>
                            <p className="text-sm text-green-600 font-bold mt-1">₹{test.SellingPrice || test.TestPrice}</p>
                          </div>
                          <button onClick={() => removeFromCompare(test.TestID)} className="text-red-500 hover:text-red-700">
                            <FaTimes size={14} />
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-50"><td className="border p-3 font-semibold">Test Type</td>{selectedForCompare.map(test => <td key={test.TestID} className="border p-3">{test.TestType || "—"}</td>)}</tr>
                  <tr><td className="border p-3 font-semibold">Sample Type</td>{selectedForCompare.map(test => <td key={test.TestID} className="border p-3">{test.SampleType || "—"}</td>)}</tr>
                  <tr className="bg-gray-50"><td className="border p-3 font-semibold">Fasting Required</td>{selectedForCompare.map(test => <td key={test.TestID} className="border p-3">{test.IsFasting ? "Yes" : "No"}</td>)}</tr>
                  <tr><td className="border p-3 font-semibold">Report Time</td>{selectedForCompare.map(test => <td key={test.TestID} className="border p-3">{test.ReportTime || "24-48 hours"}</td>)}</tr>
                  {getAllParameters().length > 0 && (
                    <>
                      <tr className="bg-gray-100"><td colSpan={selectedForCompare.length + 1} className="border p-3 font-bold text-center">Test Parameters</td></tr>
                      {getAllParameters().map(paramName => (
                        <tr key={paramName} className="hover:bg-gray-50">
                          <td className="border p-3 font-semibold">{paramName}</td>
                          {selectedForCompare.map(test => <td key={test.TestID} className="border p-3 text-sm">{getParameterValue(test, paramName)}</td>)}
                        </tr>
                      ))}
                    </>
                  )}
                  <tr className="bg-gray-100">
                    <td className="border p-3 font-bold">Price Comparison</td>
                    {selectedForCompare.map(test => {
                      const original = Number(test.TestPrice) || 0;
                      const selling = Number(test.SellingPrice) || original;
                      const discount = original - selling;
                      return (
                        <td key={test.TestID} className="border p-3">
                          <div className="space-y-1">
                            <p className="text-sm line-through text-gray-400">₹{original}</p>
                            <p className="text-lg font-bold text-green-600">₹{selling}</p>
                            {discount > 0 && <p className="text-xs text-red-500">Save ₹{discount}</p>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2">
              <button onClick={clearCompare} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Clear All</button>
              <button onClick={() => setShowCompare(false)} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">Close</button>
            </div>
          </div>
        </div>
      )}

      {selectedTestId && <TestDetails id={selectedTestId} onClose={() => setSelectedTestId(null)} />}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>
    </div>
  );
}